// AeroMax Savings Calculator — Phase 2 configurable assumptions.
// Update these values as NZ tariffs, lender terms or product specs change.
// No assumption should live hard-coded inside calc functions or UI components.

export const ASSUMPTIONS = {
  // ── Energy & tariffs (NZ defaults) ────────────────────────────────────
  electricityRateCentsPerKwh: 30,
  mainsGasDailyChargeCents: 200, // c/day fixed gas connection charge (e.g. $2.00/day)
  mainsGasVariableRateCentsPerKwh: 12,
  lpgBottleCost: 165, // NZ$ per 45 kg bottle (typical)
  lpgBottleEnergyKwhEquivalent: 580, // ~580 kWh useful per 45 kg bottle

  // ── System efficiencies (0–1) ─────────────────────────────────────────
  electricStorageEfficiency: 0.85,
  gasContinuousEfficiency: 0.80,
  gasStorageEfficiency: 0.62,
  lpgEfficiency: 0.78,

  // ── NeoPower performance ──────────────────────────────────────────────
  neoPowerCOP: 5.4,
  // Conservative real-world adjustment applied to the rated COP
  // (1.0 = use rated as-is, 0.85 = use 85% of rated for cooler conditions / mixed-mode)
  conservativeRealWorldCOPAdjustment: 0.85,

  // ── Installed cost ────────────────────────────────────────────────────
  installedCostLow: 6999,
  installedCostHigh: 7999,
  installedCostMidpoint: 7499,

  // ── Modelling horizons ────────────────────────────────────────────────
  systemLifeYears: 15,
  annualEnergyPriceIncrease: 0.04, // 4% per year energy price escalation

  // ── CO₂ factors (kg per kWh) ──────────────────────────────────────────
  co2ElectricityFactor: 0.10, // NZ grid (mostly renewable) — conservative
  co2GasFactor: 0.20,         // natural gas combustion at point-of-use

  // ── Hot-water-demand modelling constants ──────────────────────────────
  // Hot-water content of a typical shower flow (cold mix removed)
  showerHotRatio: 0.65,
  // Energy required to heat 1 L of water from 15°C → 60°C (cP·ΔT in kWh)
  energyPerHotLitreKwh: 0.0523,
  // Daily "other use" litres-per-day (dishes + basin + laundry combined)
  otherUsageLitresPerDay: { low: 25, medium: 50, high: 80 },
  // Bath/spa litres per week
  bathLitresPerWeek: { none: 0, occasional: 60, frequent: 200 },

  // ── Finance options (corrected NZ Green Loan list) ────────────────────
  // rate is decimal p.a. (0.01 = 1.00%); null = requires lender confirmation.
  financeOptions: {
    none: {
      label: "No finance / pay upfront",
      rate: null, termYears: null, cap: null, note: null,
    },
    anz: {
      label: "ANZ Good Energy Home Loan",
      rate: 0.01, termYears: 3, cap: 80000,
      note: "Subject to ANZ eligibility and current loan terms.",
    },
    asb: {
      label: "ASB Better Homes Top-Up",
      rate: 0.01, termYears: 3, cap: 80000,
      note: "Subject to ASB eligibility and current loan terms.",
    },
    bnz: {
      label: "BNZ Better Future Home Loan Top-Up",
      rate: 0.01, termYears: 3, cap: 80000,
      note: "Subject to BNZ eligibility and current loan terms.",
    },
    westpac: {
      label: "Westpac Greater Choices Home Loan",
      rate: 0.00, termYears: 5, cap: 50000,
      note: "Subject to Westpac eligibility and current loan terms.",
    },
    kiwibank: {
      label: "Kiwibank Sustainable Energy Loan",
      rate: null, termYears: 5, cap: null,
      note: "Kiwibank finance may include a sustainable energy loan/cash contribution structure depending on eligibility and current lender terms. Confirm current terms directly with Kiwibank.",
    },
    other: {
      label: "Other / not sure",
      rate: null, termYears: null, cap: null,
      note: "Finance estimate requires lender confirmation.",
    },
  },
};

// Ordered list used in the UI selector (Step 4)
export const FINANCE_OPTION_KEYS = ["none", "anz", "asb", "bnz", "westpac", "kiwibank", "other"];
